import { FunctionComponent, ReactNode, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useNavigate } from "react-router-dom";

import { UiFlowKey } from "@signalist/shared-types";

import { faArrowRight } from "@fortawesome/pro-regular-svg-icons";

import { classNames } from "@/utils/classNames";

import { Modal } from "@/components";
import { Button } from "@/components/Button/Button";
import { Checkbox } from "@/components/Checkbox/Checkbox";
import { Icon } from "@/components/Icon/Icon";
import { Tooltip } from "@/components/Tooltip/Tooltip";

import { useMarkUiFlowSeenMutation } from "@/pages/Private/redux/userUiFlow/userUiFlow.api";

/** Literal variable tokens the event row flows around — syntax, not copy, so not translated. */
const EVENT_VARIABLE_TOKENS = ["{event_name}", "{meeting_time}", "{meeting_location}"] as const;

interface OutreachExplainerModalProps {
	isOpened: boolean;
	/** The list being edited — the "Enrich only" shortcut jumps to its Settings step. */
	listId: number | string;
	/** Closes the modal — also wired to the backdrop and Escape by the shared `Modal`. */
	onClose: () => void;
}

const EventVariableToken: FunctionComponent<{ token: string }> = ({ token }) => (
	<code className="rounded bg-light-blue px-1 font-mono text-[11px] text-brand-light">{token}</code>
);

/** One labeled row of the lower section ("Chat or campaigns?" / "Event-based lists"). */
const InfoRow: FunctionComponent<{ badge: string; badgeClass: string; children: ReactNode }> = ({
	badge,
	badgeClass,
	children,
}) => (
	<div className="flex flex-col items-start gap-2 border-t border-border-light py-3 first:border-t-0 modal-s:flex-row modal-s:items-baseline modal-s:gap-3">
		<span
			className={classNames(
				"shrink-0 whitespace-nowrap rounded-full px-2.5 py-0.5 text-center text-[11px] font-semibold modal-s:min-w-[118px]",
				badgeClass
			)}
		>
			{badge}
		</span>
		<p className="text-xs leading-5 text-text-meta">{children}</p>
	</div>
);

/**
 * "Setting up outreach" — the explainer on the Outreach step (PREC-3879, mockup v12). Opened from
 * the *How outreach works* link and auto-opened by `OutreachSubHeader` until dismissed for good:
 * closing with *Don't show this again* checked records the `user_ui_flow` marker, on every close
 * path (Got it, backdrop, Escape, the enrich-only shortcut).
 */
export const OutreachExplainerModal: FunctionComponent<OutreachExplainerModalProps> = ({
	isOpened,
	listId,
	onClose,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const te = (key: string) => t(`list.outreach.explainer.${key}`);

	const [isDontShowChecked, setIsDontShowChecked] = useState<boolean>(false);
	const [markUiFlowSeen] = useMarkUiFlowSeenMutation();
	// The marker write is idempotent server-side, but still only sent once per mount.
	const markedSeenRef = useRef(false);

	const handleClose = () => {
		if (isDontShowChecked && !markedSeenRef.current) {
			markedSeenRef.current = true;
			markUiFlowSeen(UiFlowKey.OUTREACH_EXPLAINER);
		}

		onClose();
	};

	const handleEnrichOnly = () => {
		handleClose();
		navigate(`/app/lists/create/settings/${listId}`);
	};

	return (
		<Modal
			footer={
				<>
					<div className="min-w-0">
						<Checkbox
							isChecked={isDontShowChecked}
							labelClass="text-xs text-text-meta"
							testId="outreach-explainer-dont-show"
							onChange={() => setIsDontShowChecked((checked) => !checked)}
						>
							{te("dontShowAgain")}
						</Checkbox>
					</div>
					<div className="w-fit shrink-0">
						<Button title={te("gotIt")} onClick={handleClose} />
					</div>
				</>
			}
			handleClose={handleClose}
			isOpened={isOpened}
			size="md"
			title={te("title")}
		>
			<div className="flex flex-col gap-4 modal-m:flex-row">
				<div className="flex-1 rounded-xl border border-brand-light/30 bg-light-blue p-4">
					<h3 className="text-sm font-bold text-brand-light">{te("campaigns.title")}</h3>
					<p className="mt-0.5 text-xs font-medium text-brand-light">{te("campaigns.subtitle")}</p>
					<ul className="mt-3 list-disc space-y-2 pl-4 text-xs leading-5 text-text-meta marker:text-brand-light">
						<li>{te("campaigns.point1")}</li>
						<li>{te("campaigns.point2")}</li>
						<li>
							{te("campaigns.point3Prefix")}{" "}
							<Tooltip content={te("campaigns.point3Tooltip")}>
								<span className="cursor-help border-b border-dashed border-brand-light font-semibold text-brand-light">
									{te("campaigns.point3Term")}
								</span>
							</Tooltip>
						</li>
						<li>{te("campaigns.point4")}</li>
					</ul>
				</div>
				<div className="flex-1 rounded-xl border border-status-success/30 bg-status-success-light/40 p-4">
					<h3 className="text-sm font-bold text-status-success">{te("chat.title")}</h3>
					<p className="mt-0.5 text-xs font-medium text-status-success">{te("chat.subtitle")}</p>
					<ul className="mt-3 list-disc space-y-2 pl-4 text-xs leading-5 text-text-meta marker:text-status-success">
						<li>{te("chat.point1")}</li>
						<li>{te("chat.point2")}</li>
						<li>{te("chat.point3")}</li>
						<li>{te("chat.point4")}</li>
					</ul>
				</div>
			</div>

			<button
				className="mt-5 flex w-full cursor-pointer items-center gap-3 rounded-xl border border-brand-light/30 bg-light-blue px-4 py-3 text-left duration-200 hover:border-brand-light"
				type="button"
				onClick={handleEnrichOnly}
			>
				<span className="shrink-0 whitespace-nowrap rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-semibold text-text-meta">
					{te("enrichOnly.badge")}
				</span>
				<span className="flex-1 text-xs font-medium text-text">{te("enrichOnly.text")}</span>
				<Icon className="h-3.5 w-3.5 shrink-0 text-brand-light" icon={faArrowRight} />
			</button>

			<div className="mt-5">
				<InfoRow badge={te("chatOrCampaigns.badge")} badgeClass="bg-light-blue text-brand-light">
					{te("chatOrCampaigns.body")}{" "}
					<em className="not-italic font-semibold text-brand-light">
						{te("chatOrCampaigns.emphasis")}
					</em>{" "}
					{te("chatOrCampaigns.bodyEnd")}
				</InfoRow>
				<InfoRow
					badge={te("eventLists.badge")}
					badgeClass="bg-status-warning-light/60 text-status-warning"
				>
					{te("eventLists.body1")} <EventVariableToken token={EVENT_VARIABLE_TOKENS[0]} />,{" "}
					<EventVariableToken token={EVENT_VARIABLE_TOKENS[1]} /> {te("eventLists.body2")}{" "}
					<EventVariableToken token={EVENT_VARIABLE_TOKENS[2]} /> {te("eventLists.body3")}
				</InfoRow>
			</div>
		</Modal>
	);
};
